import { isTreatmentBCreditFloor } from 'store/ixp/selector';
import { TXN_MAP } from 'types/constants';

/**
 * Calculates a percentage of a balance amount, rounded down to the nearest cent.
 *
 * This helper is used throughout the credit floor workflow to calculate payment amounts
 * based on percentage buttons (50%, 75%, etc).
 *
 * @param balanceAmount - The balance amount to calculate percentage of
 * @param percentage - The percentage as a decimal (e.g., 0.5 for 50%, 0.75 for 75%)
 * @returns The calculated amount rounded down to the nearest cent (2 decimal places)
 *
 * @example
 * calculateCreditFloorAmount(100.99, 0.5)  // returns 50.49
 * calculateCreditFloorAmount(0, 0.5)       // returns 0
 * calculateCreditFloorAmount(-10, 0.5)     // returns 0 (negative amounts treated as 0)
 */
export const calculateCreditFloorAmount = (balanceAmount: number, percentage: number): number => {
  // Guard against invalid inputs
  if (!balanceAmount || balanceAmount <= 0 || !percentage || percentage < 0) {
    return 0;
  }

  // Calculate percentage and round down to nearest cent
  // Multiply by 100, floor, then divide by 100 to ensure proper rounding
  return Math.floor(balanceAmount * percentage * 100) / 100;
};

/**
 * Checks if a decline is specifically a credit floor decline with proper reason codes.
 *
 * This validates:
 * 1. User is in TreatmentB
 * 2. creditFloorDeclineAmount > 0
 * 3. Decline reason code is SMALLER_PAYMENT (lowercase or uppercase)
 *
 * This is an alias for isTreatmentBCreditFloor for better naming in page-level code.
 *
 * @param params - Object containing required data
 * @param params.ixp - IXP experiment data
 * @param params.featureFlags - Feature flags
 * @param params.payment - Payment object with status and error
 * @returns true if this is a valid credit floor decline
 */
export const isCreditFloorTrtB = ({
  ixp,
  featureFlags,
  payment,
}: {
  ixp: any;
  featureFlags: any;
  payment: any;
}): boolean => {
  // isTreatmentBCreditFloor now includes reason code checks
  return isTreatmentBCreditFloor({
    ixp,
    featureFlags,
    payment,
  });
};

/**
 * Determines if credit floor decline conditions are met for auto-edit or enhanced error messaging.
 *
 * This utility consolidates the common conditions checked across multiple pages to determine
 * if a payment decline should trigger credit floor-specific behavior (auto-edit mode or enhanced UI).
 *
 * @param params - Object containing all required conditions
 * @param params.paymentStatus - Current payment status
 * @param params.transactionType - Type of transaction (must be INVOICE)
 * @param params.isBatchPaymentsEnabled - Whether batch payments are enabled
 * @param params.isEditAmountAllowed - Whether editing amount is allowed (based on partial payment, schedule, autopay)
 * @param params.isCreditFloorDecline - Whether this is a credit floor decline (caller determines logic)
 * @returns true if all conditions are met for credit floor decline handling
 *
 * @example
 * // In pages - simple credit floor check
 * const isCreditFloorDecline = isTreatmentBCreditFloor({ ixp, featureFlags, payment });
 * if (shouldHandleCreditFloorDecline({
 *   paymentStatus,
 *   transactionType: saleType,
 *   isBatchPaymentsEnabled,
 *   isEditAmountAllowed,
 *   isCreditFloorDecline
 * })) {
 *   // Trigger auto-edit mode
 * }
 *
 * @example
 * // In pagesHelpers - complex credit floor check with reason codes
 * const isCreditFloorDecline = hasDeclineAmount &&
 *   (paymentError?.reasonCode === SMALLER_PAYMENT || paymentError?.reasonCode === SMALLER_PAYMENT_V2);
 * if (shouldHandleCreditFloorDecline({
 *   paymentStatus,
 *   transactionType,
 *   isBatchPaymentsEnabled,
 *   isEditAmountAllowed,
 *   isCreditFloorDecline
 * })) {
 *   // Show enhanced error message
 * }
 */
export const shouldHandleCreditFloorDecline = ({
  paymentStatus,
  transactionType,
  isBatchPaymentsEnabled,
  isEditAmountAllowed,
  isCreditFloorDecline,
}: {
  paymentStatus: any; // Allow flexible payment status types from Redux
  transactionType: any; // Allow flexible transaction type
  isBatchPaymentsEnabled: any; // Allow flexible boolean types
  isEditAmountAllowed: any; // Allow flexible boolean types
  isCreditFloorDecline: any; // Allow flexible types
}): boolean => {
  // Guard against null/undefined/falsy values
  if (!paymentStatus || !transactionType || typeof paymentStatus !== 'string') {
    return false;
  }

  const isPaymentDeclined = paymentStatus === TXN_MAP.STATUS.DECLINED;

  return (
    isPaymentDeclined &&
    transactionType === TXN_MAP.TYPES.INVOICE &&
    !isBatchPaymentsEnabled &&
    !!isEditAmountAllowed &&
    !!isCreditFloorDecline
  );
};
